// API Configuration for StudyMind Frontend
import { getClientId } from '../utils/clientId';

export const API_CONFIG = {
  // Use environment variable for production, fallback to localhost for development
  BASE_URL:
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://127.0.0.1:8000',
  API_VERSION: process.env.REACT_APP_API_VERSION || 'v1',
  // Default timeout for most API calls (ms)
  // Heavy endpoints like /listening/generate/ can override this.
  TIMEOUT: 600000, // 10 minutes
  // REVAMP: public, login-free build. Payments/credits/accounts are retired.
  PAYMENT_ENABLED: false,
  ACCOUNTS_ENABLED: false,
  // Maintenance mode control
  MAINTENANCE_MODE: false,
  MAINTENANCE_ALLOW_LIST: ['test@test.com'],
  // Shared client header sent on every /api/ call (must match the backend
  // FRONTEND_CLIENT_SECRET). A speed-bump against cross-site/curl API abuse.
  FRONTEND_CLIENT_SECRET:
    process.env.REACT_APP_FRONTEND_CLIENT_SECRET || 'studymind-public-client-dev',
};

// Debug logging to see what BASE_URL is being used
console.log('🔧 API_CONFIG.BASE_URL:', API_CONFIG.BASE_URL);
console.log('🔧 API_CONFIG.PAYMENT_ENABLED:', API_CONFIG.PAYMENT_ENABLED);

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  REGISTER: '/api/users/register/',
  REQUEST_OTP: '/api/users/request-otp/',
  LOGIN: '/api/users/login/',
  PASSWORD_LOGIN: '/api/users/password-login/',
  PASSWORD_UPDATE: '/api/users/password-update/',
  LOGOUT: '/api/users/logout/',
  TOKEN_REFRESH: '/api/users/token/refresh/',
  CSRF_TOKEN: '/api/users/csrf/',
  TEST_SESSION: '/api/users/test-session/',
  
  // User Profile
  PROFILE: '/api/users/profile/',
  TRANSACTIONS: '/api/users/transactions/',
  REDEEM_PROMO: '/api/users/redeem-promo-code/',
  DELETE_ACCOUNT: '/api/users/delete-account/',
  
  // Credit Management
  ADD_CREDITS: '/api/users/add-credits/',
  DEDUCT_CREDITS: '/api/users/deduct-credits/',
  
  // Payment
  CREATE_PAYMENT_SESSION: '/api/users/create-payment-session/',
  PAYMENT_LINK: '/api/users/payment-link/',
  GET_PAYMENT_LINK: '/api/users/payment-link/',
  PAYMENT_STATUS: (sessionId: string) => `/api/users/payment-status/${sessionId}/`,
  
  // Notes Generation
  NOTES_GENERATE: '/api/recite/notes/generate/',
  NOTES_CHECK_CREDIT_COST: '/api/recite/notes/check-credit-cost/',
  
  // Document Upload
  UPLOAD_DOCUMENT: '/api/recite/upload-document/',
  OCR_DOCUMENT: '/api/recite/ocr-document/',
  EXTRACT_DOCUMENT: '/api/recite/extract-document/',
  
  // Flashcards
  FLASHCARDS_GENERATE: '/api/recite/generate-flashcards/',
  FLASHCARDS_FROM_PDF: '/api/recite/flashcards/generate-from-pdf/',
  FLASHCARDS_GENERATE_CHUNKED: '/api/recite/flashcards/generate-chunked/',
  FLASHCARDS_CHECK_CREDIT_COST: '/api/recite/flashcards/check-credit-cost/',
  FLASHCARDS_LIST: '/api/recite/flashcards/',
  FLASHCARD_DETAIL: '/api/recite/flashcards/',
  FLASHCARD_DELETE: '/api/recite/flashcards/',
  FLASHCARD_SET: (id: number) => `/api/recite/flashcard-sets/${id}/`,
  CREATE_FLASHCARD_SET: '/api/recite/flashcard-sets/create/',
  DELETE_FLASHCARD_SET: (id: number) => `/api/recite/flashcard-sets/${id}/delete/`,
  CLEAR_FLASHCARD_SET: (id: number) => `/api/recite/flashcard-sets/${id}/clear/`,
  CREATE_FLASHCARD: (setId: number) => `/api/recite/flashcard-sets/${setId}/flashcards/create/`,
  UPDATE_FLASHCARD: (cardId: number) => `/api/recite/flashcards/${cardId}/`,
  DELETE_FLASHCARD: (cardId: number) => `/api/recite/flashcards/${cardId}/delete/`,
  
  // Study Sessions
  STUDY_SESSIONS: '/api/recite/study-sessions/',
  STUDY_SESSION_DETAIL: '/api/recite/study-sessions/',
  STUDY_SESSION_CREATE: '/api/recite/study-sessions/',
  STUDY_HISTORY: '/api/recite/study-history/',
  START_STUDY_SESSION: (id: number) => `/api/recite/flashcard-sets/${id}/start-session/`,
  SUBMIT_ANSWER: (sessionId: number) => `/api/recite/sessions/${sessionId}/submit-answer/`,
  END_STUDY_SESSION: (sessionId: number) => `/api/recite/sessions/${sessionId}/end/`,
  EXPIRE_STUDY_SESSION: (sessionId: number) => `/api/recite/sessions/${sessionId}/expire/`,
  
  // Chat
  CHAT_SESSIONS: '/api/recite/chat-sessions/',
  CHAT_SESSION_DETAIL: '/api/recite/chat-sessions/',
  CHAT_SESSION_CREATE: '/api/recite/chat-sessions/',
  CHAT_MESSAGE: '/api/recite/chat-message/',
  
  // Understanding Module
  UNDERSTANDING_CHAT: '/api/recite/understanding/chat/',
  UNDERSTANDING_CHAT_SESSIONS: '/api/recite/understanding/chat-sessions/',
  UPDATE_CHAT_SESSION: (id: number) => `/api/recite/understanding/chat-sessions/${id}/update/`,
  DELETE_CHAT_SESSION: (id: number) => `/api/recite/understanding/chat-sessions/${id}/delete/`,
  
  // Writing Module
  WRITING_GRADE: '/api/recite/writing/grade/',
  
  // Exam Module
  EXAM_GENERATE: '/api/recite/exam/generate/',
  EXAM_PAPER_GENERATE: '/api/recite/exam/paper/',
  EXAM_SETS: '/api/recite/exam-sets/',
  EXAM_SET: (id: number) => `/api/recite/exam-sets/${id}/`,
  DELETE_EXAM_SET: (id: number) => `/api/recite/exam-sets/${id}/delete/`,
  UPDATE_EXAM_SET_TITLE: (id: number) => `/api/recite/exam-sets/${id}/title/`,
  START_EXAM_SESSION: (id: number) => `/api/recite/exam-sets/${id}/start-session/`,
  END_EXAM_SESSION: (sessionId: number) => `/api/recite/exam-sessions/${sessionId}/end/`,
  EXAM_HISTORY: '/api/recite/exam-history/',
  COMPARE_ANSWER: '/api/recite/compare-answer/',
  COMPARE_ANSWER_BATCH: '/api/recite/compare-answer-batch/',
  
  // Listening
  LISTENING_UPLOAD: '/api/listening/upload/',
  LISTENING_HISTORY: '/api/listening/history/',
  LISTENING_SCORE: '/api/listening/score/',
  LISTENING_GENERATE: '/api/listening/generate/',
  LISTENING_SET: (id: number | string) => `/api/listening/sets/${id}/`,
  LISTENING_SUBMIT: (id: number | string) => `/api/listening/sets/${id}/submit/`,
  LISTENING_DELETE: (id: number | string) => `/api/listening/sets/${id}/delete/`,
  LISTENING_SETS: '/api/listening/sets/',
  LISTENING_UPDATE_TITLE: (id: number | string) => `/api/listening/sets/${id}/title/`,
  
  // QR Code
  SEND_QR_CODE: '/api/users/send-qr-code/',
  DOWNLOAD_QR_CODE: '/api/users/download-qr-code/',

  // Speaking
  SPEAKING_TOPICS: '/api/speaking/topics/',
  SPEAKING_RANDOM_TOPIC: '/api/speaking/topics/random/',
  SPEAKING_CHAT_START: '/api/speaking/chat/start/',
  SPEAKING_CHAT_REPLY: (id: number | string) => `/api/speaking/chat/${id}/reply/`,
  SPEAKING_CHAT_END: (id: number | string) => `/api/speaking/chat/${id}/end/`,
  SPEAKING_PRESENT_START: '/api/speaking/present/start/',
  SPEAKING_PRESENT_SUBMIT: (id: number | string) => `/api/speaking/present/${id}/submit/`,
  SPEAKING_PRESENT_END: (id: number | string) => `/api/speaking/present/${id}/end/`,
  SPEAKING_SHADOW_START: '/api/speaking/shadow/start/',
  SPEAKING_SHADOW_SUBMIT: (id: number | string) => `/api/speaking/shadow/${id}/submit/`,
  SPEAKING_SHADOW_END: (id: number | string) => `/api/speaking/shadow/${id}/end/`,

  // Lecture
  LECTURE_UPLOAD: '/api/lecture/upload/',
  LECTURE_LIST: '/api/lecture/list/',
  LECTURE_DETAIL: (id: number | string) => `/api/lecture/${id}/`,
  LECTURE_CONTENT: (id: number | string) => `/api/lecture/${id}/content/`,
  LECTURE_PDF: (id: number | string) => `/api/lecture/${id}/pdf/`,
  LECTURE_DELETE: (id: number | string) => `/api/lecture/${id}/delete/`,
  LECTURE_UPDATE: (id: number | string) => `/api/lecture/${id}/update/`,
  LECTURE_STATS: '/api/lecture/stats/',
};

// API Response Types
export interface ApiResponse<T = any> {
  data?: T;
  message?: string;
  error?: string;
  status: number;
}

export interface User {
  email: string;
  first_name: string;
  last_name: string;
  study_goal?: string;
  credits: number;
  date_joined: string;
  email_verified: boolean;
}

export interface UserProfile extends User {
  recent_transactions: CreditTransaction[];
}

export interface CreditTransaction {
  id: number;
  transaction_type: 'PURCHASE' | 'USAGE' | 'REFUND' | 'BONUS';
  amount: number;
  description: string;
  created_at: string;
  reference_id?: string;
}

export interface LoginRequest {
  email: string;
  otp_code: string;
}

export interface RegisterRequest {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirm_password: string;
}

export interface OTPRequest {
  email: string;
}

export interface CreditRequest {
  amount: number;
  description?: string;
}

export interface PaymentSessionRequest {
  amount_hkd: number;
  credits_to_award: number;
}

export interface PaymentSessionResponse {
  session_id: string;
  checkout_url: string;
  payment_session_id: number;
}

export interface PaymentLinkResponse {
  payment_url: string;
  amount_hkd: number;
  credits_to_award: number;
  conversion_rate: number;
}

export interface PaymentStatusResponse {
  session_id: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  amount_hkd: number;
  credits_to_award: number;
  created_at: string;
  completed_at?: string;
}

// Recite Module Types
export interface Flashcard {
  id: number;
  question: string;
  answer: string;
  created_at: string;
  times_studied: number;
  last_studied: string | null;
  difficulty_level: 'easy' | 'medium' | 'hard';
  is_mastered: boolean;
}

export interface FlashcardSet {
  id: number;
  title: string;
  document_name: string;
  card_count: number;
  created_at: string;
  last_studied: string | null;
  progress: number;
  is_active: boolean;
  flashcards: Flashcard[];
}

export interface StudySession {
  id: number;
  flashcard_set: number;
  started_at: string;
  ended_at: string | null;
  cards_studied: number;
  total_cards: number;
}

export interface FlashcardStats {
  total_cards: number;
  studied_cards: number;
  mastered_cards: number;
  progress_percentage: number;
  total_study_sessions: number;
  last_studied: string | null;
  total_study_time: number;
}

export interface CreateFlashcardSetRequest {
  title: string;
  document_name: string;
  document_content: string;
}

export interface SubmitAnswerRequest {
  flashcard_id: number;
  is_correct: boolean;
  time_spent?: number;
}

// Listening Module Types
export interface ListeningQuestion {
  id: string;
  question: string;
  type: 'mcq' | 'tf' | 'short';
  options?: string[];
  correct_answer: string;
}

export interface ListeningSet {
  id: number;
  title: string;
  // Stateless public build: the server stores no audio, so this is absent;
  // the browser plays the locally-uploaded file via an object URL instead.
  audio_file?: string;
  transcript: string;
  questions: ListeningQuestion[];
  created_at?: string;
  size_mb?: number;
}

export interface ListeningSetListResponse {
  sets: ListeningSet[];
  storage_mb: number;
  storage_limit_mb: number;
}

// Speaking Module Types
export interface SpeakingChatMessage {
  role: 'assistant' | 'user';
  type?: string;
  speech?: string;
  question_optional?: string;
}

export interface SpeakingChatSession {
  id: number;
  topic: string;
  mode: 'chatting';
  messages: SpeakingChatMessage[];
  user_transcripts: { t: string }[];
  started_at: string;
  ended_at?: string | null;
}

export interface PresentingSession {
  id: number;
  topic: string;
  questions: { q: string; detail?: string }[];
  answers: { transcript: string }[];
  report?: any;
  created_at: string;
}

export interface ShadowingSession {
  id: number;
  topic: string;
  script: any;
  submissions: { turn?: number; transcript: string }[];
  feedback?: any;
  created_at: string;
}

// API Utility Functions
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const getApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// ---------------------------------------------------------------------------
// REVAMP: global fetch guard.
// Several modules call fetch() directly. We wrap window.fetch so EVERY request
// to /api/ automatically carries the shared X-Frontend-Client header (the
// backend rejects calls without it), includes credentials, and drops any stale
// "Authorization: Bearer null" that the pre-revamp code used to send (an
// invalid bearer token would otherwise make DRF return 401).
// ---------------------------------------------------------------------------
let _fetchPatched = false;
export function installApiFetchGuard(): void {
  if (_fetchPatched || typeof window === 'undefined' || !window.fetch) return;
  _fetchPatched = true;

  // Public build: drop any leftover JWT from a previous logged-in session so it
  // can't trigger 401s.
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  } catch {
    /* ignore */
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.toString()
        : input.url;

    if (!url.includes('/api/')) {
      return originalFetch(input, init);
    }

    const baseHeaders =
      init.headers ||
      (typeof input !== 'string' && !(input instanceof URL) ? input.headers : undefined);
    const headers = new Headers(baseHeaders as HeadersInit | undefined);
    headers.set('X-Frontend-Client', API_CONFIG.FRONTEND_CLIENT_SECRET);

    // REVAMP: the public, login-free build uses NO authentication. Always drop any
    // Authorization header — including a stale JWT left in localStorage by a
    // previous logged-in session, which would otherwise make DRF return 401.
    headers.delete('Authorization');

    const nextInit: RequestInit = {
      ...init,
      headers,
      credentials: init.credentials || 'include',
    };

    const isGeneration =
      /\/(generate|generate-flashcards|flashcards\/generate-chunked|notes\/generate|writing\/grade|understanding\/chat|chat\/start|present\/start|shadow\/start|analyze|upload\/)/.test(
        url
      ) && (init.method || 'GET').toUpperCase() === 'POST';

    return originalFetch(input, nextInit).then((res) => {
      // After a successful AI generation, nudge the (frequency-capped) interstitial.
      if (isGeneration && res.ok && typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new Event('studymind:generation-done'));
        } catch {
          /* ignore */
        }
      }
      return res;
    });
  };
}

installApiFetchGuard();

export const getCsrfToken = (): string | null => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Function to fetch CSRF token from server
export const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    console.log('Fetching CSRF token from:', getApiUrl('/api/users/csrf/'));
    const response = await fetch(getApiUrl('/api/users/csrf/'), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-cache',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    console.log('CSRF response status:', response.status);
    console.log('CSRF response headers:', Object.fromEntries(response.headers.entries()));
    
    if (response.ok) {
      const data = await response.json();
      console.log('CSRF token received:', data.csrfToken ? 'Success' : 'Failed');
      return data.csrfToken || null;
    } else {
      console.error('CSRF token fetch failed with status:', response.status);
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }
  return null;
};

export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<ApiResponse<T>> => {
  const url = getApiUrl(endpoint);

  // REVAMP: public build — no JWT. The shared client header identifies our SPA.
  // (Authorization is left in place for a future re-introduction of accounts.)
  const accessToken = localStorage.getItem('access_token');

  // Always ensure we have a CSRF token before making any request
  let csrfToken = getCsrfToken();
  if (!csrfToken) {
    csrfToken = await fetchCsrfToken().catch(() => null);
  }

  const defaultOptions: RequestInit = {
    credentials: 'include',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      // REVAMP: shared frontend-client guard header (see backend middleware).
      'X-Frontend-Client': API_CONFIG.FRONTEND_CLIENT_SECRET,
      // Stable per-visitor id for daily per-client limits (e.g. listening cap).
      'X-Client-Id': getClientId(),
      ...(csrfToken && { 'X-CSRFToken': csrfToken }),
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...options.headers,
    },
    ...options,
  };

  // If body is FormData, remove automatic JSON content-type
  if (defaultOptions.body instanceof FormData) {
    // @ts-ignore
    delete defaultOptions.headers['Content-Type'];
  }

  // ------------------------------------------------------------------
  // Use AbortController to enforce timeout (fetch has no native timeout).
  // ------------------------------------------------------------------
  const controller = new AbortController();
  const timeoutMs = options.timeout ?? API_CONFIG.TIMEOUT;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Attach signal AFTER merging defaultOptions to avoid mutating caller
  const fetchOptions: RequestInit = { ...defaultOptions, signal: controller.signal };

  try {
    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);

    // Check if we need to refresh CSRF token on 403 errors
    if (response.status === 403) {
      const newCsrfToken = await fetchCsrfToken();
      if (newCsrfToken) {
        defaultOptions.headers = {
          ...defaultOptions.headers,
          'X-CSRFToken': newCsrfToken,
        };
        const retryResponse = await fetch(url, defaultOptions);
        if (retryResponse.ok) {
          return handleResponse(retryResponse);
        }
      }
    }
    
    return handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    console.error('API request error:', error);
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(0, 'Network error', error);
  }
};

// Helper function to handle response parsing
const handleResponse = async <T = any>(response: Response): Promise<ApiResponse<T>> => {
  let data;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = {};
  }

  if (!response.ok) {
    console.error('API request failed:', {
      status: response.status,
      data: data
    });
    throw new ApiError(response.status, data.error || data.message || 'API request failed', data);
  }

  return {
    data,
    status: response.status,
    message: data.message,
  };
};

// Specific API functions
export const api = {
  // Authentication
  register: async (data: RegisterRequest): Promise<ApiResponse> => {
    return apiRequest(API_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  testSession: async (): Promise<ApiResponse> => {
    return apiRequest(API_ENDPOINTS.TEST_SESSION);
  },

  requestOTP: async (data: OTPRequest): Promise<ApiResponse> => {
    return apiRequest(API_ENDPOINTS.REQUEST_OTP, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  login: async (data: LoginRequest): Promise<ApiResponse<{ user: User; access_token: string; refresh_token: string }>> => {
    // 1. Clear any old/stale tokens first to prevent conflicts
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // 2. Force fetch a fresh CSRF token before login attempt
    console.log('🔄 Fetching fresh CSRF token before login...');
    await fetchCsrfToken();

    console.log('Login API call - cookies before:', document.cookie);
    const response = await apiRequest(API_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    console.log('Login API call - cookies after:', document.cookie);
    
    // Store JWT tokens if login successful
    if (response.data?.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      console.log('JWT tokens stored in localStorage');
    }
    
    return response;
  },

  passwordLogin: async (data: { email: string; password: string }): Promise<ApiResponse<{ user: User; access_token: string; refresh_token: string }>> => {
    // 1. Clear any old/stale tokens first to prevent conflicts
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    
    // 2. Force fetch a fresh CSRF token before password login...
    console.log('🔄 Fetching fresh CSRF token before password login...');
    await fetchCsrfToken();

    console.log('Password Login API call - cookies before:', document.cookie);
    const response = await apiRequest(API_ENDPOINTS.PASSWORD_LOGIN, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    console.log('Password Login API call - cookies after:', document.cookie);
    
    // Store JWT tokens if login successful
    if (response.data?.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      console.log('JWT tokens stored in localStorage');
    }
    
    return response;
  },

  updatePassword: async (data: { new_password: string; confirm_password: string }): Promise<ApiResponse> => {
    return apiRequest(API_ENDPOINTS.PASSWORD_UPDATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  refreshToken: async (refresh_token: string): Promise<ApiResponse<{ access_token: string; refresh_token: string }>> => {
    const response = await apiRequest(API_ENDPOINTS.TOKEN_REFRESH, {
      method: 'POST',
      body: JSON.stringify({ refresh_token }),
    });
    
    // Update stored tokens if refresh successful
    if (response.data?.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      console.log('JWT tokens refreshed and stored');
    }
    
    return response;
  },

  logout: async (): Promise<ApiResponse> => {
    // Get refresh token before clearing it
    const refreshToken = localStorage.getItem('refresh_token');
    
    const response = await apiRequest(API_ENDPOINTS.LOGOUT, {
      method: 'POST',
      body: refreshToken ? JSON.stringify({ refresh_token: refreshToken }) : undefined,
    });
    
    // Clear JWT tokens on logout
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    console.log('JWT tokens cleared from localStorage');
    
    return response;
  },

  // User Management
  getProfile: async (): Promise<ApiResponse<UserProfile>> => {
    console.log('GetProfile API call - cookies before:', document.cookie);
    const response = await apiRequest(API_ENDPOINTS.PROFILE);
    console.log('GetProfile API call - cookies after:', document.cookie);
    return response;
  },

  updateProfile: async (data: Partial<User>): Promise<ApiResponse<UserProfile>> => {
    return apiRequest(API_ENDPOINTS.PROFILE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getTransactions: async (): Promise<ApiResponse<{ results: CreditTransaction[] }>> => {
    return apiRequest(API_ENDPOINTS.TRANSACTIONS);
  },

  // Credit Management
  addCredits: async (data: CreditRequest): Promise<ApiResponse<{ new_balance: number }>> => {
    return apiRequest(API_ENDPOINTS.ADD_CREDITS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deductCredits: async (data: CreditRequest): Promise<ApiResponse<{ new_balance: number }>> => {
    return apiRequest(API_ENDPOINTS.DEDUCT_CREDITS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Test authentication
  checkAuth: async (): Promise<ApiResponse> => {
    return apiRequest('/api/users/check-auth/');
  },

  // Simple auth test
  simpleAuthTest: async (): Promise<ApiResponse> => {
    return apiRequest('/api/users/simple-auth-test/');
  },

  // Document text extraction. mode:
  //  - 'text'   fast embedded-text extraction (PDF/TXT/PPTX)
  //  - 'vision' AI reads pages/images (scanned PDFs, photos, diagrams/figures)
  extractDocument: async (
    file: File,
    mode: 'text' | 'vision' = 'text',
    instruction?: string
  ): Promise<ApiResponse<{ extracted_text: string; processing_method?: string; page_count?: number }>> => {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    if (instruction) form.append('instruction', instruction);
    // Images can only be read visually.
    if (file.type.startsWith('image/')) form.set('mode', 'vision');
    return apiRequest(API_ENDPOINTS.EXTRACT_DOCUMENT, {
      method: 'POST',
      body: form,
      timeout: 10 * 60 * 1000,
    });
  },

  // Listening Module API
  generateListening: async (formData: FormData): Promise<ApiResponse<ListeningSet>> => {
    return apiRequest(API_ENDPOINTS.LISTENING_GENERATE, {
      method: 'POST',
      body: formData,
      timeout: 15 * 60 * 1000, // 15-minute timeout for heavy audio processing
    });
  },

  getListeningSet: async (id: number | string): Promise<ApiResponse<ListeningSet>> => {
    return apiRequest(API_ENDPOINTS.LISTENING_SET(id));
  },

  submitListeningAnswers: async (
    id: number | string,
    answers: Record<string, string>,
    questions: ListeningQuestion[]
  ): Promise<ApiResponse<{ score: any; results: any[] }>> => {
    // Stateless build: the answer key lives only in the browser, so the
    // questions must be posted alongside the answers for server-side grading.
    return apiRequest(API_ENDPOINTS.LISTENING_SUBMIT(id), {
      method: 'POST',
      body: JSON.stringify({ questions, answers }),
    });
  },

  deleteListeningSet: async (id: number | string): Promise<ApiResponse> => {
    return apiRequest(API_ENDPOINTS.LISTENING_DELETE(id), { method: 'DELETE' });
  },

  getListeningSets: async (): Promise<ApiResponse<ListeningSetListResponse>> => {
    return apiRequest(API_ENDPOINTS.LISTENING_SETS);
  },

  updateListeningSetTitle: async (
    id: number | string,
    title: string
  ): Promise<ApiResponse<{ title: string }>> => {
    return apiRequest(API_ENDPOINTS.LISTENING_UPDATE_TITLE(id), {
      method: 'POST',
      body: JSON.stringify({ title }),
    });
  },

  // Speaking Module API
  getSpeakingTopics: async (): Promise<ApiResponse<{ topics: string[] }>> => {
    return apiRequest(API_ENDPOINTS.SPEAKING_TOPICS);
  },

  getSpeakingRandomTopic: async (): Promise<ApiResponse<{ topic: string }>> => {
    return apiRequest(API_ENDPOINTS.SPEAKING_RANDOM_TOPIC);
  },

  speakingStartChat: async (
    topic: string,
    durationMinutes: number = 60,
    languageCodes?: string[]
  ): Promise<ApiResponse<SpeakingChatSession & { ai?: any; max_minutes?: number }>> => {
    const payload: any = { topic, duration_minutes: durationMinutes };
    if (languageCodes && languageCodes.length) payload.language_codes = languageCodes.join(',');
    return apiRequest(API_ENDPOINTS.SPEAKING_CHAT_START, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  speakingChatReply: async (
    sessionId: number | string,
    audioBlob: Blob,
    languageCodes?: string[],
    context?: { topic?: string; history?: Array<{ assistant: string; user: string }> }
  ): Promise<ApiResponse<{ transcript: string; ai: any; audio_url?: string }>> => {
    // REVAMP: stateless — send topic + prior turns so the AI keeps context.
    const form = new FormData();
    form.append('file', audioBlob, `reply_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.webm`);
    if (languageCodes && languageCodes.length) form.append('language_codes', languageCodes.join(','));
    if (context?.topic) form.append('topic', context.topic);
    if (context?.history) form.append('history', JSON.stringify(context.history));
    return apiRequest(API_ENDPOINTS.SPEAKING_CHAT_REPLY(sessionId), {
      method: 'POST',
      body: form,
      timeout: 10 * 60 * 1000,
    });
  },

  speakingEndChat: async (
    sessionId: number | string,
    payload?: { topic: string; turns: Array<{ question: string; user_transcript: string }> }
  ): Promise<ApiResponse<{ farewell: string; farewell_audio_url?: string; report: any }>> => {
    // REVAMP: the stateless evaluate endpoint holds no session — it needs the
    // topic and every Q/A turn in the body to run the AI review. When payload is
    // omitted (e.g. abandoning a session on leave) no AI evaluation is triggered.
    return apiRequest(API_ENDPOINTS.SPEAKING_CHAT_END(sessionId), {
      method: 'POST',
      body: payload ? JSON.stringify(payload) : undefined,
    });
  },

  speakingStartPresent: async (topic: string, languageCodes?: string[]): Promise<ApiResponse<PresentingSession>> => {
    return apiRequest(API_ENDPOINTS.SPEAKING_PRESENT_START, {
      method: 'POST',
      body: JSON.stringify({ topic, ...(languageCodes && languageCodes.length ? { language_codes: languageCodes.join(',') } : {}) }),
    });
  },

  speakingSubmitPresent: async (
    sessionId: number | string,
    answers: { a1?: Blob; a2?: Blob; a3?: Blob },
    languageCodes?: string[],
    context?: { topic?: string; questions?: { q: string; detail?: string }[] }
  ): Promise<ApiResponse<PresentingSession>> => {
    // REVAMP: stateless — the endpoint keeps no session, so topic + questions
    // must accompany the audio for the AI to evaluate each answer.
    const form = new FormData();
    if (answers.a1) form.append('a1', answers.a1, 'a1.webm');
    if (answers.a2) form.append('a2', answers.a2, 'a2.webm');
    if (answers.a3) form.append('a3', answers.a3, 'a3.webm');
    if (languageCodes && languageCodes.length) form.append('language_codes', languageCodes.join(','));
    if (context?.topic) form.append('topic', context.topic);
    if (context?.questions) form.append('questions', JSON.stringify(context.questions));
    return apiRequest(API_ENDPOINTS.SPEAKING_PRESENT_SUBMIT(sessionId), {
      method: 'POST',
      body: form,
      timeout: 15 * 60 * 1000,
    });
  },

  speakingEndPresent: async (sessionId: number | string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest(API_ENDPOINTS.SPEAKING_PRESENT_END(sessionId), { method: 'POST' });
  },

  speakingStartShadow: async (topic: string, languageCodes?: string[]): Promise<ApiResponse<ShadowingSession>> => {
    return apiRequest(API_ENDPOINTS.SPEAKING_SHADOW_START, {
      method: 'POST',
      body: JSON.stringify({ topic, ...(languageCodes && languageCodes.length ? { language_codes: languageCodes.join(',') } : {}) }),
    });
  },

  speakingSubmitShadow: async (
    sessionId: number | string,
    audioBlob: Blob,
    turn?: number
  ): Promise<ApiResponse<ShadowingSession>> => {
    const form = new FormData();
    form.append('file', audioBlob, 'shadow.webm');
    if (typeof turn === 'number') form.append('turn', String(turn));
    return apiRequest(API_ENDPOINTS.SPEAKING_SHADOW_SUBMIT(sessionId), {
      method: 'POST',
      body: form,
      timeout: 10 * 60 * 1000,
    });
  },

  speakingEndShadow: async (sessionId: number | string): Promise<ApiResponse<{ message: string }>> => {
    return apiRequest(API_ENDPOINTS.SPEAKING_SHADOW_END(sessionId), { method: 'POST' });
  },

  // Promotion
  redeemPromo: async (code: string): Promise<ApiResponse<{ credit_added: number; new_balance: number }>> => {
    return apiRequest(API_ENDPOINTS.REDEEM_PROMO, {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  // Account Management
  deleteAccount: async (): Promise<ApiResponse> => {
    return apiRequest(API_ENDPOINTS.DELETE_ACCOUNT, {
      method: 'POST',
    });
  },

  // Stripe Payment
  createPaymentSession: async (data: PaymentSessionRequest): Promise<ApiResponse<PaymentSessionResponse>> => {
    return apiRequest(API_ENDPOINTS.CREATE_PAYMENT_SESSION, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getPaymentLink: async (packageType?: string): Promise<ApiResponse<PaymentLinkResponse>> => {
    const url = packageType 
        ? `${API_ENDPOINTS.GET_PAYMENT_LINK}?package_type=${packageType}`
        : API_ENDPOINTS.GET_PAYMENT_LINK;
    return apiRequest(url);
  },

  getPaymentStatus: async (sessionId: string): Promise<ApiResponse<PaymentStatusResponse>> => {
    return apiRequest(API_ENDPOINTS.PAYMENT_STATUS(sessionId));
  },


}; 